import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DialogTitle } from "@/components/ui/dialog";
import { ArrowUp, ArrowDown, Pencil, Trash } from "lucide-react";

interface Video {
  id: number;
  videoId: string;
  title: string;
  thumbnail: string;
  sortOrder: number;
}

interface VideoManagerProps {
  categoryId: number;
  categoryName: string;
}

export default function VideoManager({ categoryId, categoryName }: VideoManagerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [videoUrl, setVideoUrl] = useState("");
  const [editingVideo, setEditingVideo] = useState<Video | null>(null);

  // Fetch videos for this category
  const { data: videos } = useQuery<Video[]>({
    queryKey: [`/api/categories/${categoryId}/videos`],
  });

  // Add video mutation
  const addVideo = useMutation({
    mutationFn: async (values: { videoUrl: string }) => {
      const res = await fetch(`/api/categories/${categoryId}/videos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/categories/${categoryId}/videos`] });
      toast({ title: "Video added successfully" });
      setVideoUrl("");
    },
    onError: (error) => {
      toast({
        title: "Error adding video",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update video mutation
  const updateVideo = useMutation({
    mutationFn: async (video: Video) => {
      const res = await fetch(`/api/categories/${categoryId}/videos/${video.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(video),
      });

      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/categories/${categoryId}/videos`] });
      toast({ title: "Video updated successfully" });
      setEditingVideo(null);
    },
    onError: (error) => {
      toast({
        title: "Error updating video",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete video mutation
  const deleteVideo = useMutation({
    mutationFn: async (videoId: number) => {
      const res = await fetch(`/api/categories/${categoryId}/videos/${videoId}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/categories/${categoryId}/videos`] });
      toast({ title: "Video deleted successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error deleting video",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Reorder video mutation
  const reorderVideo = useMutation({
    mutationFn: async ({ videoId, direction }: { videoId: number; direction: 'up' | 'down' }) => {
      const res = await fetch(`/api/categories/${categoryId}/videos/${videoId}/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ direction }),
      });

      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/categories/${categoryId}/videos`] });
    },
    onError: (error) => {
      toast({
        title: "Error reordering video",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <DialogTitle>Manage Videos for {categoryName}</DialogTitle>
      
      <div className="space-y-2">
        <Label htmlFor="videoUrl">Add YouTube Video</Label>
        <div className="flex gap-2">
          <Input
            id="videoUrl"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            className="bg-zinc-800 border-zinc-700"
          />
          <Button
            onClick={() => videoUrl && addVideo.mutate({ videoUrl })}
            disabled={!videoUrl}
          >
            Add Video
          </Button>
        </div>
      </div>

      {videos && videos.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Thumbnail</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {videos.map((video, index) => (
              <TableRow key={video.id}>
                <TableCell>
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-24 aspect-video object-cover rounded"
                  />
                </TableCell>
                <TableCell>
                  {editingVideo?.id === video.id ? (
                    <Input
                      value={editingVideo.title}
                      onChange={(e) => setEditingVideo({ ...editingVideo, title: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  ) : (
                    video.title
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => reorderVideo.mutate({ videoId: video.id, direction: 'up' })}
                      disabled={index === 0}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => reorderVideo.mutate({ videoId: video.id, direction: 'down' })}
                      disabled={index === videos.length - 1}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {editingVideo?.id === video.id ? (
                      <>
                        <Button
                          variant="ghost"
                          onClick={() => updateVideo.mutate(editingVideo)}
                        >
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => setEditingVideo(null)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingVideo(video)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this video?')) {
                          deleteVideo.mutate(video.id);
                        }
                      }}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <p className="text-muted-foreground text-center py-4">No videos added yet</p>
      )}
    </div>
  );
}
